import React, { useEffect, useState, useRef } from "react";

export default function KshetraResultPage() {
  const [sessions, setSessions] = useState([]);
  const [session, setSession] = useState("");
  const [kshetraList, setKshetraList] = useState([]);
  const [events, setEvents] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    event_code: "",
    age: "",
    type: "",
    kshetra: "",
  });
  const printRef = useRef(null);

  // Fetch latest session and generate list from 2017-2018
  useEffect(() => {
    fetch("/api/home/session-name")
      .then(res => res.json())
      .then(latest => {
        let [start] = latest.split('-').map(Number);
        let sessionList = [];
        for (let y = 2017; y <= start; ++y) {
          sessionList.push(`${y}-${y + 1}`);
        }
        setSessions(sessionList);
        setSession(latest);
      })
      .catch(() => {
        let now = new Date();
        let year = now.getMonth() < 7 ? now.getFullYear() - 1 : now.getFullYear();
        let sessionList = [];
        for (let y = 2017; y <= year; ++y) {
          sessionList.push(`${y}-${y + 1}`);
        }
        setSessions(sessionList);
        setSession(sessionList[sessionList.length - 1]);
      });
  }, []);

  // Fetch Kshetra list
  useEffect(() => {
    fetch("/api/result/kshetra-list")
      .then(res => res.json())
      .then(list => setKshetraList(Array.isArray(list) ? list : []))
      .catch(() => setKshetraList([]));
  }, []);

  // Fetch event list whenever session changes
  useEffect(() => {
    setFilters(f => ({ ...f, event_code: "" })); // Reset event on session change
    setEvents([]);
    if (!session) return;
    fetch(`/api/result/events-list?session=${session}`)
      .then(res => res.json())
      .then(data => setEvents(Array.isArray(data) ? data : []))
      .catch(() => setEvents([]));
  }, [session]);

  // Fetch results when filters or session change
  useEffect(() => {
    if (!filters.event_code) {
      setResults([]);
      return;
    }
    setLoading(true);
    setError("");
    const params = [
      `event_code=${encodeURIComponent(filters.event_code)}`
    ];
    if (filters.age) params.push(`age=${encodeURIComponent(filters.age)}`);
    if (filters.type) params.push(`type=${encodeURIComponent(filters.type)}`);
    if (filters.kshetra) params.push(`kshetra=${encodeURIComponent(filters.kshetra)}`);
    const url = `https://localhost:44363/api/result/event-results?${params.join("&")}`;
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch results");
        return res.json();
      })
      .then(data => {
        setResults(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message || "Unknown error");
        setLoading(false);
      });
  }, [filters, session]);

  // Print handler
  function handlePrint() {
    if (!printRef.current) return;
    const printContent = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=650");
    win.document.write("<html><head><title>Print Result</title></head><body>");
    win.document.write(printContent);
    win.document.write("</body></html>");
    win.document.close();
    win.focus();
    win.print();
    win.close();
  }

  function handleChange(e) {
    setFilters((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }
  function handleSessionChange(e) {
    setSession(e.target.value);
  }

  // Find current event details for printing summary
  const currentEvent = filters.event_code
    ? events.find(ev => ev.eventCode === filters.event_code)
    : null;

  return (
    <section id="kshetra-results" style={{ marginTop: 100, minHeight: 600 }}>
      <div className="container">
        <h2>RESULT</h2>
        {/* Session Dropdown */}
        <div className="row" style={{ marginBottom: 16 }}>
          <div className="col-md-3 col-sm-6">
            <select
              className="form-control"
              value={session}
              onChange={handleSessionChange}
            >
              <option value="">Select Session</option>
              {sessions.map((sess, i) => (
                <option value={sess} key={i}>{sess}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Filters */}
        <div className="row" style={{ marginBottom: 24 }}>
          {/* Event Dropdown */}
          <div className="col-md-3 col-sm-6">
            <select
              className="form-control"
              name="event_code"
              value={filters.event_code}
              onChange={handleChange}
              disabled={!session}
            >
              <option value="">Select Event</option>
              {events.map((ev, i) => (
                <option value={ev.eventCode} key={i}>
                  {ev.eventName} ({ev.eventCode})
                </option>
              ))}
            </select>
          </div>
          {/* Kshetra Dropdown */}
          <div className="col-md-3 col-sm-6">
            <select
              className="form-control"
              name="kshetra"
              value={filters.kshetra}
              onChange={handleChange}
              disabled={!filters.event_code}
            >
              <option value="">Kshetra</option>
              {kshetraList.map((k, i) => (
                <option value={k} key={i}>{k}</option>
              ))}
            </select>
          </div>
          {/* Age Dropdown */}
          <div className="col-md-3 col-sm-6">
            <select
              className="form-control"
              name="age"
              value={filters.age}
              onChange={handleChange}
              disabled={!filters.event_code}
            >
              <option value="">Age</option>
              <option value="U11">U11</option>
              <option value="U14">U14</option>
              <option value="U17">U17</option>
              <option value="U19">U19</option>
            </select>
          </div>
          {/* Type Dropdown */}
          <div className="col-md-2 col-sm-6">
            <select
              className="form-control"
              name="type"
              value={filters.type}
              onChange={handleChange}
              disabled={!filters.event_code}
            >
              <option value="">Type</option>
              <option value="BOY">Boy</option>
              <option value="GIRL">Girl</option>
            </select>
          </div>
          {/* Print Button */}
          <div className="col-md-1 col-sm-6">
            <button
              className="btn btn-default"
              onClick={handlePrint}
              disabled={!results.length}
            >
              Print
            </button>
          </div>
        </div>

        {/* === Event Summary === */}
        {currentEvent && (
          <div style={{
            marginBottom: 24,
            background: "#f5f5f5",
            borderRadius: 8,
            padding: "18px 18px 10px 18px",
            border: "1px solid #ddd",
            maxWidth: 650
          }}>
            <h4 style={{ marginBottom: 5 }}>
              <b>Event:</b> {currentEvent.eventName}
            </h4>
            <div>
              <b>Venue:</b> {currentEvent.venue || "-"}
            </div>
            <div>
              <b>Session:</b> {session}
            </div>
          </div>
        )}

        {/* Results Table */}
        <div style={{ width: "100%", overflowX: "auto" }} ref={printRef}>
          <table
            className="table table-bordered white-bg"
            style={{ minWidth: 1100, fontSize: 15 }}
          >
            <thead>
              <tr>
                <th>SNo.</th>
                <th>Name / School / State</th>
                <th>Photo</th>
                <th>Age Group | Type</th>
                <th>Position</th>
                <th>Performance</th>
                <th>Event</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>Loading...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={7} style={{ color: "red" }}>
                    {error}
                  </td>
                </tr>
              ) : results.length === 0 ? (
                <tr>
                  <td colSpan={7}>No results found.</td>
                </tr>
              ) : (
                results.map((r, idx) => (
                  <tr key={r.sNo || idx}>
                    <td>{r.sNo}</td>
                    <td>
                      {r.name} / {r.school} / <b>{r.state}</b>
                    </td>
                    <td>
                      {r.photoBase64 ? (
                        <img
                          src={`data:image/jpg;base64,${r.photoBase64}`}
                          alt="pic"
                          style={{ height: 75, width: 100, objectFit: "cover" }}
                        />
                      ) : (
                        "-"
                      )}
                    </td>
                    <td>
                      {r.ageGroup} | {r.type}
                    </td>
                    <td>{r.position}</td>
                    <td>{r.performance}</td>
                    <td>{r.event}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
